import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { LoginService } from '@auth/services/login/login.service';
import { StorageService } from '@shared/services/storage/storage.service';
import { ToastrService } from '@shared/services/toastr/toastr.service';
import { catchError, Observable, switchMap, throwError } from 'rxjs';

const Unauthorized = 401;

export const ErrorInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<any>> => {
  const toast = inject(ToastrService);
  const auth = inject(LoginService);
  const storage = inject(StorageService);

  return next(req).pipe(
    catchError((errorResponse: HttpErrorResponse) => {
      return handleHttpError(errorResponse, req, next, auth, storage, toast);
    }),
  );
};

function handleHttpError(
  errorResponse: HttpErrorResponse,
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  auth: LoginService,
  storage: StorageService,
  toast: ToastrService,
): Observable<HttpEvent<any>> {
  const { error, status, statusText, url } = errorResponse;
  let message = 'Unexpected error occurred';

  if (typeof error === 'string') {
    message = error;
  } else if (error && error.message) {
    message = error.message;
  } else if (statusText) {
    message = statusText;
  }

  if (status === Unauthorized) {
    if (url?.includes('/Auth/RefreshToken')) {
      console.error('Refresh token failed. Logging out.');
      auth.logout();
      toast.error('Session expired. Please login again.');
      return throwError(() => message);
    }

    return handleUnauthorized(req, next, auth, storage, toast, message);
  }

  toast.error(message);
  return throwError(() => message);
}

function handleUnauthorized(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  auth: LoginService,
  storage: StorageService,
  toast: ToastrService,
  message: string,
): Observable<HttpEvent<any>> {
  return auth.refreshToken().pipe(
    switchMap(({ token, refresh_token }) => {
      storage.setItem('token', token);
      storage.setItem('refresh_token', refresh_token);

      const newReq = req.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
        },
      });

      return next(newReq);
    }),

    catchError((refreshError) => {
      console.error('Refresh token failed:', refreshError);
      auth.logout();
      toast.error('Session expired. Please login again.');
      return throwError(() => message);
    }),
  );
}
